import React, { useState, useRef, useEffect } from "react";
import { Send } from "lucide-react";
import { PageHeader, ChatBubble, TypingIndicator, nowTime, fmtTime } from "../components/Shared";
import { translations } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AIChat({ guest, navigate, lang }) {
  const T = translations[lang];
  const fullName = `${guest.title} ${guest.name}`;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [showChips, setShowChips] = useState(true);
  const scrollRef = useRef(null);
  const streamingIdx = useRef(null);

  // Load history on mount (restore old conversations)
  useEffect(() => {
    const welcome = { role: "assistant", text: T.aiWelcome(fullName), timestamp: nowTime() };
    (async () => {
      try {
        const res = await fetch(`${API}/ai/history/${guest.sessionId}`);
        const hist = await res.json();
        if (Array.isArray(hist) && hist.length > 0) {
          const mapped = hist.map((h) => ({ role: h.role, text: h.text, timestamp: fmtTime(h.timestamp) }));
          setMessages([welcome, ...mapped]);
          setShowChips(!hist.some((h) => h.role === "user"));
        } else {
          setMessages([welcome]);
        }
      } catch {
        setMessages([welcome]);
      }
    })();
    // eslint-disable-next-line
  }, [guest.sessionId, lang]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, typing]);

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || typing) return;
    setShowChips(false);
    setInput("");
    setMessages((m) => [...m, { role: "user", text, timestamp: nowTime() }]);
    setTyping(true);

    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: guest.sessionId,
          message: text,
          guest_name: fullName,
          room_number: guest.room,
          lang,
        }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let started = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        if (!started) {
          started = true;
          setTyping(false);
          setMessages((m) => {
            streamingIdx.current = m.length;
            return [...m, { role: "assistant", text: acc, timestamp: nowTime() }];
          });
        } else {
          setMessages((m) => {
            const copy = [...m];
            copy[streamingIdx.current] = { ...copy[streamingIdx.current], text: acc };
            return copy;
          });
        }
      }
      if (!started) {
        setTyping(false);
        setMessages((m) => [...m, { role: "assistant", text: acc || T.aiError, timestamp: nowTime() }]);
      }
    } catch (e) {
      setTyping(false);
      setMessages((m) => [...m, { role: "assistant", text: T.aiError, timestamp: nowTime() }]);
    }
  };

  return (
    <div className="flex flex-col h-screen" data-testid="ai-chat-screen">
      <PageHeader
        title={T.aiTitle}
        onBack={() => navigate("dashboard")}
        subtitle={<span className="inline-block text-[11px] bg-white/20 px-2 py-0.5 rounded-full mt-0.5">{T.poweredBy}</span>}
      />

      <div ref={scrollRef} className="flex-1 overflow-y-auto hide-scrollbar bg-[#F7FAFC] px-4 py-4 flex flex-col gap-3">
        {messages.map((m, i) => (
          <ChatBubble key={i} role={m.role} text={m.text} timestamp={m.timestamp} />
        ))}

        {showChips && (
          <div className="flex flex-wrap gap-2 mt-1">
            {T.suggestions.map((s) => (
              <button
                key={s}
                data-testid="ai-suggestion-chip"
                onClick={() => send(s)}
                className="text-[13px] bg-white border border-[#C9E4F5] text-[#3898C7] rounded-full px-3.5 py-2 shadow-sm active:scale-95 transition-transform"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {typing && <TypingIndicator label={T.aiTyping} />}
      </div>

      <div className="bg-white border-t border-[#F0F7FF] px-3 py-3 pb-[calc(76px+env(safe-area-inset-bottom,0px))]">
        <div className="flex items-center gap-2">
          <input
            data-testid="ai-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={T.askPlaceholder}
            className="flex-1 bg-[#F0F7FF] border border-[#E0E8F0] rounded-full h-[46px] px-4 text-[15px] text-[#1A1A2E] placeholder-[#6B7B8D] focus:outline-none focus:border-[#4AADDF] focus:ring-[3px] focus:ring-[#4AADDF]/15 transition-all"
          />
          <button
            data-testid="ai-send-button"
            onClick={() => send()}
            disabled={!input.trim() || typing}
            className="w-[46px] h-[46px] rounded-full bg-[#4AADDF] text-white flex items-center justify-center shrink-0 active:scale-90 transition-transform disabled:opacity-50"
          >
            <Send size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}
